import { useRouter } from 'expo-router';
import { getErrorScreen } from 'features/auth/hooks/useAcceptChallenge';
import useResendOtp, {
  ResendOtpData,
} from 'features/otp/hooks/useResendOtp/useResendOtp';
import { InformationScreenTypeEnum } from 'features/otp/information/InformationScreenTypeEnum';
import { useContext, useState } from 'react';
import { getLambdaHeaders } from 'shared/hooks/auth/useLambdaHeaders';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import { navigateToMaxLimitError } from 'shared/utils/errors/navigateToMaxLimitError';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

export function useResendEmail() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);

  const resendOtp = useResendOtp();
  const { getVaroSessionId, idToken, fetchIovationToken } =
    useContext<AuthContextProps>(AuthContext);

  const resendEmail = async (data: ResendOtpData) => {
    setLoading(true);

    const aToken = await fetchIovationToken();
    const varoSessionId = await getVaroSessionId();
    const headers = await getLambdaHeaders(idToken, varoSessionId);

    setIsOtpSent(false);

    try {
      const { message, errorCode, title } = await resendOtp(
        {
          ...data,
          aToken,
        },
        headers,
      );

      setLoading(false);
      setIsOtpSent(true);

      if (errorCode) {
        router.navigate({
          pathname: '/otp/error',
          params: {
            screenType: getErrorScreen(String(errorCode)),
            message: message,
            title: title,
          },
        });
      }

      return { success: true };
    } catch (error) {
      setLoading(false);
      setIsOtpSent(false);

      if (navigateToMaxLimitError(router, error)) {
        return { success: false };
      }

      logErrorWithContext('ResendOTP error: ' + error, {
        featureArea: FeatureArea.AUTHENTICATION,
        responsibleTeam: ResponsibleTeam.FRAUD,
        severity: ErrorSeverity.CRITICAL,
      });

      router.navigate({
        pathname: '/otp/error',
        params: { screenType: InformationScreenTypeEnum.Error },
      });
      return { success: false };
    }
  };

  return {
    resendEmail,
    loading,
    isOTPSent: isOtpSent,
  };
}
